import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as Collapsible from '@radix-ui/react-collapsible';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Select from '@radix-ui/react-select';
import { observer } from 'mobx-react-lite';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useFamilyStore } from '@/hooks/useStores';
import { relationshipsApi } from '@/api/relationshipsWrapper';
import { petsApi } from '@/api/petsWrapper';
import type { PetResponse } from 'we-tree-api-client/v2';
import CollapsibleChevron from '@/components/shared/CollapsibleChevron';
import Close from '@/icons/Close';
import Plus from '@/icons/Plus';
import { PersonCard } from '@/components/person/PersonCard';
import { PetCard } from '@/components/pet/PetCard';
import ProfileSectionCount from '@/components/person/ProfileSectionCount';
import type { PersonType } from '@/stores/models/Person';
import type { RelationshipType } from '@/stores/models/Relationship';

import type { ProfileLocationState } from '@/types/profile-navigation';
import {
  getParentChildLabel,
  PARENT_CHILD_RELATIONSHIP_OPTIONS,
  type ParentChildRelationshipTypeValue
} from '@/types/parent-child-relationship';
import { PET_OWNER_ROLE_LABELS, PET_OWNER_ROLE_OPTIONS, type PetOwnerRoleValue } from '@/types/pet-owner';
import './person-relationships.css';

interface PersonRelationshipsProps {
  familyMemberId: string;
  familyId?: string;
  readonly?: boolean;
  defaultExpanded?: boolean;
  variant?: 'default' | 'profile';
  sectionMode?: 'collapsible' | 'tab-panel';
}

const PersonRelationships: React.FC<PersonRelationshipsProps> = observer(
  ({
    familyMemberId,
    familyId,
    readonly = false,
    defaultExpanded = true,
    variant = 'default',
    sectionMode = 'collapsible'
  }) => {
    const { t } = useTranslation('forms');
    const { t: tCommon } = useTranslation('common');
    const isProfileVariant = variant === 'profile';
    const isTabPanel = sectionMode === 'tab-panel';
    const familyStore = useFamilyStore();
    const navigate = useNavigate();
    const location = useLocation();
    const { familyId: familyIdParam } = useParams<{ familyId: string }>();
    const effectiveFamilyId = familyId ?? familyIdParam ?? '';

    const profileLocationState = location.state as ProfileLocationState | undefined;

    const [expanded, setExpanded] = useState(defaultExpanded);
    const [error, setError] = useState<string | null>(null);
    const [savingRelationshipId, setSavingRelationshipId] = useState<string | null>(null);
    const [savingPetOwnerKey, setSavingPetOwnerKey] = useState<string | null>(null);
    const [pets, setPets] = useState<PetResponse[]>([]);
    const [petsLoading, setPetsLoading] = useState(false);
    const parentRelationships = familyStore.getParentRelationships(familyMemberId);
    const spouseRelationships = familyStore.getSpouseRelationships(familyMemberId);
    const childRelationships = familyStore.getChildRelationships(familyMemberId);

    useEffect(() => {
      if (!effectiveFamilyId || !familyMemberId) return;
      let cancelled = false;
      setPetsLoading(true);
      petsApi.getPetsByOwner(familyMemberId).then((result) => {
        if (cancelled) return;
        setPetsLoading(false);
        if (result.success) {
          setPets(result.data.pets ?? []);
        } else {
          setPets([]);
        }
      });
      return () => {
        cancelled = true;
      };
    }, [familyMemberId, effectiveFamilyId]);

    const returnTo = `${location.pathname}${location.search}`;

    const handleAddPet = () => {
      navigate(`/families/${effectiveFamilyId}/pets/add?parentId=${familyMemberId}`, {
        state: { ...profileLocationState, returnTo }
      });
    };

    const navStateForAdd = {
      state: { ...profileLocationState, returnTo }
    };

    const handleAddNewParent = () => {
      navigate(`/families/${effectiveFamilyId}/people/add?childId=${familyMemberId}&type=parent`, navStateForAdd);
    };

    const handleAddNewChild = () => {
      navigate(`/families/${effectiveFamilyId}/people/add?parentId=${familyMemberId}&type=child`, navStateForAdd);
    };

    const handleAddSpouse = () => {
      navigate(`/families/${effectiveFamilyId}/people/add?parentId=${familyMemberId}&type=spouse`, navStateForAdd);
    };

    const handlePersonProfileClick = (targetFamilyMemberId: string) => {
      navigate(`/families/${effectiveFamilyId}/people/${targetFamilyMemberId}`, {
        state: profileLocationState
      });
    };

    const handleTreeViewClick = (targetFamilyMemberId: string) => {
      navigate(`/families/${effectiveFamilyId}/hybrid-pedigree/${targetFamilyMemberId}`);
    };

    const handlePetProfileClick = (petFamilyMemberId: string) => {
      navigate(`/families/${effectiveFamilyId}/pets/${petFamilyMemberId}`, {
        state: profileLocationState
      });
    };

    const relationshipCount =
      parentRelationships.length + spouseRelationships.length + childRelationships.length + pets.length;

    const refreshPetsForOwner = async () => {
      if (!effectiveFamilyId || !familyMemberId) return;
      const petsResult = await petsApi.getPetsByOwner(familyMemberId);
      if (petsResult.success) {
        setPets(petsResult.data.pets ?? []);
      }
    };

    const handlePetOwnerRoleChange = async (
      petFamilyMemberId: string,
      petOwnerId: string,
      newRole: PetOwnerRoleValue
    ) => {
      if (!effectiveFamilyId) return;
      setError(null);
      setSavingPetOwnerKey(`${petFamilyMemberId}:${petOwnerId}`);
      try {
        const result = await petsApi.updatePetOwner(petFamilyMemberId, petOwnerId, { role: newRole });
        if (result.success) {
          await familyStore.loadFamilyData(effectiveFamilyId, true);
          await refreshPetsForOwner();
        } else {
          setError(
            result.error?.getUserMessage?.() ?? result.error?.message ?? t('relationships.failedUpdateOwnerType')
          );
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : t('relationships.failedUpdateOwnerType'));
      } finally {
        setSavingPetOwnerKey(null);
      }
    };

    const handleRemovePetOwner = async (petFamilyMemberId: string, petOwnerId: string) => {
      if (!effectiveFamilyId) return;
      if (!window.confirm(t('relationships.confirmRemovePet'))) return;
      setError(null);
      setSavingPetOwnerKey(`${petFamilyMemberId}:${petOwnerId}`);
      try {
        const result = await petsApi.removePetOwner(petFamilyMemberId, petOwnerId);
        if (result.success) {
          await familyStore.loadFamilyData(effectiveFamilyId, true);
          await refreshPetsForOwner();
        } else {
          setError(
            result.error?.getUserMessage?.() ?? result.error?.message ?? t('relationships.failedRemovePetOwner')
          );
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : t('relationships.failedRemovePetOwner'));
      } finally {
        setSavingPetOwnerKey(null);
      }
    };

    const handleRelationshipTypeChange = async (relationshipId: string, newType: ParentChildRelationshipTypeValue) => {
      if (!effectiveFamilyId) return;
      setError(null);
      setSavingRelationshipId(relationshipId);
      try {
        const result = await relationshipsApi.updateRelationship(relationshipId, {
          relationshipType: newType
        });
        if (result.success) {
          await familyStore.loadFamilyData(effectiveFamilyId, true);
        } else {
          setError(result.error?.getUserMessage?.() ?? result.error?.message ?? t('relationships.failedUpdate'));
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : t('relationships.failedUpdate'));
      } finally {
        setSavingRelationshipId(null);
      }
    };

    const handleRemoveRelationship = async (relationshipId: string) => {
      if (!effectiveFamilyId) return;
      if (!window.confirm(t('relationships.confirmRemove'))) return;
      setError(null);
      setSavingRelationshipId(relationshipId);
      try {
        const result = await relationshipsApi.deleteRelationship(relationshipId);
        if (result.success) {
          await familyStore.loadFamilyData(effectiveFamilyId, true);
        } else {
          setError(result.error?.getUserMessage?.() ?? result.error?.message ?? t('relationships.failedRemove'));
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : t('relationships.failedRemove'));
      } finally {
        setSavingRelationshipId(null);
      }
    };

    const roleLabel = (role: 'parent' | 'child') =>
      role === 'parent' ? t('relationships.roleParent') : t('relationships.roleChild');

    const renderSpouseRow = (spouse: PersonType, relationship: RelationshipType) => {
      const isSaving = savingRelationshipId === relationship.id;
      return (
        <div key={relationship.id} className="person-relationships__row">
          {!readonly ? (
            <>
              <span className="person-relationships__relationship-type-static">{t('relationships.spouse')}</span>
              <span className="person-relationships__name">{spouse.fullName}</span>
              <button
                type="button"
                onClick={() => handleRemoveRelationship(relationship.id)}
                disabled={isSaving}
                className="person-relationships__remove-button"
                aria-label={t('relationships.removeAsSpouseAria', { name: spouse.fullName })}
              >
                {tCommon('remove')}
              </button>
            </>
          ) : (
            <>
              <span className="person-relationships__relationship-type-static">{t('relationships.spouse')}</span>
              <span className="person-relationships__name">{spouse.fullName}</span>
            </>
          )}
        </div>
      );
    };

    const renderRelationshipRow = (person: PersonType, relationship: RelationshipType, role: 'parent' | 'child') => {
      const relType = relationship.relationshipType as ParentChildRelationshipTypeValue;
      const isSaving = savingRelationshipId === relationship.id;
      return (
        <div key={relationship.id} className="person-relationships__row">
          {!readonly ? (
            <>
              <Select.Root
                value={relType}
                onValueChange={(v) =>
                  handleRelationshipTypeChange(relationship.id, v as ParentChildRelationshipTypeValue)
                }
                disabled={isSaving}
              >
                <Select.Trigger
                  className="person-relationships__relationship-type-select"
                  aria-label={t('relationships.relationshipTypeAria', { name: person.fullName })}
                >
                  <Select.Value />
                </Select.Trigger>
                <Select.Portal>
                  <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                    {PARENT_CHILD_RELATIONSHIP_OPTIONS.map((opt) => (
                      <Select.Item key={opt.value} value={opt.value} className="form-select-item">
                        <Select.ItemText>{opt.label}</Select.ItemText>
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Portal>
              </Select.Root>
              <span className="person-relationships__name">{person.fullName}</span>
              <button
                type="button"
                onClick={() => handleRemoveRelationship(relationship.id)}
                disabled={isSaving}
                className="person-relationships__remove-button"
                aria-label={t('relationships.removeAsRoleAria', {
                  name: person.fullName,
                  role: roleLabel(role)
                })}
              >
                {tCommon('remove')}
              </button>
            </>
          ) : (
            <>
              <span className="person-relationships__type-label">{getParentChildLabel(relType)}</span>
              <span className="person-relationships__name">{person.fullName}</span>
            </>
          )}
        </div>
      );
    };

    const renderPetRow = (pet: PetResponse) => {
      const ownerLink = pet.owners?.find((o) => o.ownerFamilyMemberId === familyMemberId);
      const relType = ownerLink?.role as PetOwnerRoleValue | undefined;
      const isSaving = savingPetOwnerKey === `${pet.familyMemberId}:${ownerLink?.id}`;

      if (!readonly && ownerLink && relType != null) {
        return (
          <div key={pet.familyMemberId} className="person-relationships__row">
            <Select.Root
              value={relType}
              onValueChange={(v) => handlePetOwnerRoleChange(pet.familyMemberId, ownerLink.id, v as PetOwnerRoleValue)}
              disabled={isSaving}
            >
              <Select.Trigger
                className="person-relationships__relationship-type-select"
                aria-label={t('relationships.ownerTypeAria', { name: pet.displayName })}
              >
                <Select.Value />
              </Select.Trigger>
              <Select.Portal>
                <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                  {PET_OWNER_ROLE_OPTIONS.map((opt) => (
                    <Select.Item key={opt.value} value={opt.value} className="form-select-item">
                      <Select.ItemText>{opt.label}</Select.ItemText>
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Portal>
            </Select.Root>
            <span className="person-relationships__name">{pet.displayName}</span>
            <button
              type="button"
              onClick={() => handleRemovePetOwner(pet.familyMemberId, ownerLink.id)}
              disabled={isSaving}
              className="person-relationships__remove-button"
              aria-label={t('relationships.removePetAria', { name: pet.displayName })}
            >
              {tCommon('remove')}
            </button>
          </div>
        );
      }

      return (
        <div key={pet.familyMemberId} className="person-relationships__row">
          {relType != null ? (
            <span className="person-relationships__type-label">{PET_OWNER_ROLE_LABELS[relType]}</span>
          ) : (
            <span className="person-relationships__type-label">{tCommon('emptyDefault')}</span>
          )}
          <span className="person-relationships__name">{pet.displayName}</span>
        </div>
      );
    };

    const renderProfileRemoveX = (label: string, onClick: () => void, disabled: boolean) => (
      <button
        type="button"
        className="card-hover-remove"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        title={label}
      >
        <Close width={15} height={15} />
      </button>
    );

    const renderProfileParentChildCard = (
      person: PersonType,
      relationship: RelationshipType,
      role: 'parent' | 'child'
    ) => {
      const relType = relationship.relationshipType as ParentChildRelationshipTypeValue;
      const isSaving = savingRelationshipId === relationship.id;
      const showLinkBadge = relType !== 'biological';

      return (
        <div key={relationship.id} className="profile-rels__card-wrap">
          {!readonly &&
            renderProfileRemoveX(
              t('relationships.removeAsRoleAria', { name: person.fullName, role: roleLabel(role) }),
              () => handleRemoveRelationship(relationship.id),
              isSaving
            )}
          <div className="profile-rels__card-surface">
            <PersonCard
              familyMemberId={person.familyMemberId}
              generation={0}
              layout="members"
              isTreeAnchor={false}
              onTreeViewClick={handleTreeViewClick}
              onProfileClick={handlePersonProfileClick}
            />
            {showLinkBadge && (
              <span className="profile-rels__link-badge profile-rels__link-badge--dashed">
                {getParentChildLabel(relType)}
              </span>
            )}
          </div>
        </div>
      );
    };

    const renderProfileSpouseCard = (spouse: PersonType, relationship: RelationshipType) => {
      const isSaving = savingRelationshipId === relationship.id;

      return (
        <div key={relationship.id} className="profile-rels__card-wrap">
          {!readonly &&
            renderProfileRemoveX(
              t('relationships.removeAsSpouseAria', { name: spouse.fullName }),
              () => handleRemoveRelationship(relationship.id),
              isSaving
            )}
          <PersonCard
            familyMemberId={spouse.familyMemberId}
            generation={0}
            layout="members"
            isTreeAnchor={false}
            onTreeViewClick={handleTreeViewClick}
            onProfileClick={handlePersonProfileClick}
          />
        </div>
      );
    };

    const renderProfilePetCard = (pet: PetResponse) => {
      const ownerLink = pet.owners?.find((o) => o.ownerFamilyMemberId === familyMemberId);
      const relType = ownerLink?.role as PetOwnerRoleValue | undefined;
      const isSaving = savingPetOwnerKey === `${pet.familyMemberId}:${ownerLink?.id}`;

      return (
        <div key={pet.familyMemberId} className="profile-rels__card-wrap profile-rels__card-wrap--pet">
          {!readonly &&
            ownerLink &&
            renderProfileRemoveX(
              t('relationships.removePetFromListAria', { name: pet.displayName }),
              () => handleRemovePetOwner(pet.familyMemberId, ownerLink.id),
              isSaving
            )}
          <div className="profile-rels__card-surface">
            <PetCard pet={pet} familyId={effectiveFamilyId} onProfileClick={handlePetProfileClick} />
            {relType != null && (
              <span className="profile-rels__link-badge profile-rels__role-badge">
                {PET_OWNER_ROLE_LABELS[relType]}
              </span>
            )}
          </div>
        </div>
      );
    };

    const renderProfileRelationshipsBody = () => (
      <div className="profile-rels members-cards-scope">
        <div className="profile-rels__group">
          <div className="profile-rels__sub-head">
            <h3 className="profile-rels__sub-title">
              {t('relationships.parents')} <span className="profile-rels__sub-count">{parentRelationships.length}</span>
            </h3>
            {!readonly && effectiveFamilyId && (
              <button type="button" className="btn btn--ghost btn--sm" onClick={handleAddNewParent}>
                <Plus width={13} height={13} />
                {t('person.addParent')}
              </button>
            )}
          </div>
          {parentRelationships.length === 0 ? (
            <p className="profile-rels__empty">{t('relationships.noParents')}</p>
          ) : (
            <div className="profile-rels__grid members-grid">
              {parentRelationships.map(({ parent, relationship }) =>
                renderProfileParentChildCard(parent, relationship, 'parent')
              )}
            </div>
          )}
        </div>

        <div className="profile-rels__group">
          <div className="profile-rels__sub-head">
            <h3 className="profile-rels__sub-title">
              {spouseRelationships.length === 1 ? t('relationships.spouse') : t('relationships.spouses')}{' '}
              <span className="profile-rels__sub-count">{spouseRelationships.length}</span>
            </h3>
            {!readonly && effectiveFamilyId && (
              <button type="button" className="btn btn--ghost btn--sm" onClick={handleAddSpouse}>
                <Plus width={13} height={13} />
                {t('person.addSpouse')}
              </button>
            )}
          </div>
          {spouseRelationships.length === 0 ? (
            <p className="profile-rels__empty">{t('relationships.noSpouses')}</p>
          ) : (
            <div className="profile-rels__grid members-grid">
              {spouseRelationships.map(({ spouse, relationship }) => renderProfileSpouseCard(spouse, relationship))}
            </div>
          )}
        </div>

        <div className="profile-rels__group">
          <div className="profile-rels__sub-head">
            <h3 className="profile-rels__sub-title">
              {t('relationships.children')} <span className="profile-rels__sub-count">{childRelationships.length}</span>
            </h3>
            {!readonly && effectiveFamilyId && (
              <button type="button" className="btn btn--ghost btn--sm" onClick={handleAddNewChild}>
                <Plus width={13} height={13} />
                {t('person.addChild')}
              </button>
            )}
          </div>
          {childRelationships.length === 0 ? (
            <p className="profile-rels__empty">{t('relationships.noChildren')}</p>
          ) : (
            <div className="profile-rels__grid members-grid">
              {childRelationships.map(({ child, relationship }) =>
                renderProfileParentChildCard(child, relationship, 'child')
              )}
            </div>
          )}
        </div>

        <div className="profile-rels__group">
          <div className="profile-rels__sub-head">
            <h3 className="profile-rels__sub-title">
              {t('relationships.pets')} <span className="profile-rels__sub-count">{pets.length}</span>
            </h3>
            {!readonly && effectiveFamilyId && (
              <button type="button" className="btn btn--ghost btn--sm" onClick={handleAddPet}>
                <Plus width={13} height={13} />
                {t('person.addPet')}
              </button>
            )}
          </div>
          {petsLoading ? (
            <p className="profile-rels__empty">{t('relationships.loadingPets')}</p>
          ) : pets.length === 0 ? (
            <p className="profile-rels__empty">{t('relationships.noPets')}</p>
          ) : (
            <div className="profile-rels__grid members-grid">{pets.map((pet) => renderProfilePetCard(pet))}</div>
          )}
        </div>
      </div>
    );

    const sectionBody = (
      <div className={isProfileVariant ? 'person-relationships__body' : 'person-relationships__content'}>
        {error && (
          <div className="person-relationships__error" role="alert">
            <span>{error}</span>
            <button
              type="button"
              onClick={() => setError(null)}
              className="person-relationships__error-dismiss"
              aria-label={t('actions.dismiss')}
            >
              ×
            </button>
          </div>
        )}

        {isProfileVariant ? (
          renderProfileRelationshipsBody()
        ) : (
          <>
            <div className="person-relationships__section">
              <div className="person-relationships__section-header">
                <h3 className="person-relationships__section-title">{t('relationships.parents')}</h3>
              </div>
              {parentRelationships.length === 0 && (
                <p className="person-relationships__empty-hint">{t('relationships.noParents')}</p>
              )}
              {parentRelationships.map(({ parent, relationship }) =>
                renderRelationshipRow(parent, relationship, 'parent')
              )}
            </div>

            <div className="person-relationships__section">
              <div className="person-relationships__section-header">
                <h3 className="person-relationships__section-title">{t('relationships.spouses')}</h3>
              </div>
              {spouseRelationships.length === 0 && (
                <p className="person-relationships__empty-hint">{t('relationships.noSpouses')}</p>
              )}
              {spouseRelationships.map(({ spouse, relationship }) => renderSpouseRow(spouse, relationship))}
            </div>

            <div className="person-relationships__section">
              <div className="person-relationships__section-header">
                <h3 className="person-relationships__section-title">{t('relationships.children')}</h3>
              </div>
              {childRelationships.length === 0 && (
                <p className="person-relationships__empty-hint">{t('relationships.noChildren')}</p>
              )}
              {childRelationships.map(({ child, relationship }) => renderRelationshipRow(child, relationship, 'child'))}
            </div>

            <div className="person-relationships__section">
              <div className="person-relationships__section-header">
                <h3 className="person-relationships__section-title">{t('relationships.pets')}</h3>
              </div>
              {petsLoading ? (
                <p className="person-relationships__empty-hint">{t('relationships.loadingPets')}</p>
              ) : pets.length === 0 ? (
                <p className="person-relationships__empty-hint">{t('relationships.noPets')}</p>
              ) : (
                pets.map((pet) => renderPetRow(pet))
              )}
            </div>
          </>
        )}
      </div>
    );

    if (isTabPanel) {
      return (
        <div className="person-relationships person-relationships--profile person-relationships--tab-panel">
          <div className="tab-panel-card__head">
            <h2 className="tab-panel-card__title">{t('person.tabs.relationships')}</h2>
          </div>
          <div className="tab-panel-card__body">{sectionBody}</div>
        </div>
      );
    }

    return (
      <Collapsible.Root
        open={expanded}
        onOpenChange={setExpanded}
        className={isProfileVariant ? 'person-relationships person-relationships--profile' : 'person-relationships'}
      >
        <div className="person-relationships__header">
          <Collapsible.Trigger asChild>
            <button type="button" className="person-relationships__header-button">
              <CollapsibleChevron className="person-relationships__chevron" />
              <h2 className="person-relationships__title">
                {t('person.tabs.relationships')}
                {isProfileVariant && <ProfileSectionCount count={relationshipCount} />}
              </h2>
            </button>
          </Collapsible.Trigger>
          {!readonly && effectiveFamilyId && !isProfileVariant && (
            <div className="person-relationships__header-actions">
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <button
                    type="button"
                    className="person-relationships__add-button"
                    aria-haspopup="menu"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t('person.addPersonPlus')}
                  </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content
                    className="person-relationships__add-menu-content"
                    sideOffset={4}
                    align="end"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DropdownMenu.Item
                      className="person-relationships__add-menu-item"
                      onSelect={() => handleAddNewParent()}
                    >
                      {t('person.addParent')}
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                      className="person-relationships__add-menu-item"
                      onSelect={() => handleAddNewChild()}
                    >
                      {t('person.addChild')}
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                      className="person-relationships__add-menu-item"
                      onSelect={() => handleAddSpouse()}
                    >
                      {t('person.addSpouse')}
                    </DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
              <button
                type="button"
                className="person-relationships__add-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddPet();
                }}
              >
                {t('person.addPetPlus')}
              </button>
            </div>
          )}
        </div>

        <Collapsible.Content>{sectionBody}</Collapsible.Content>
      </Collapsible.Root>
    );
  }
);

export default PersonRelationships;
